/**
 * MediaPickerModal — compact media library picker for inline use inside
 * block editors. Fetches `/api/media`, lets the admin search + pick an
 * existing image, or upload a new one on the fly. Returns the selected
 * URL via `onSelect(url)` and closes.
 *
 * Usage:
 *   <MediaPickerModal open={open} onClose={close} onSelect={url => …} />
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Search, Check, Loader } from 'lucide-react';
import axios from 'axios';
import { withAdminAuth } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MediaPickerModal = ({ open, onClose, onSelect }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/media`);
      // Newest first — the API returns unordered, so sort here.
      const items = (res.data?.media || []).filter((m) =>
        (m.type || '').startsWith('image') || /\.(png|jpe?g|webp|gif|svg)$/i.test(m.filename || m.path || '')
      );
      items.sort((a, b) => String(b.uploadedAt || '').localeCompare(String(a.uploadedAt || '')));
      setMedia(items);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchMedia();
  }, [open, fetchMedia]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      Array.from(files).forEach((f) => formData.append('files', f));
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/media/upload-multiple`,
        formData,
        withAdminAuth({ headers: { 'Content-Type': 'multipart/form-data' } }),
      );
      const uploaded = res.data?.files || [];
      if (uploaded.length > 0) {
        // Auto-select the freshly uploaded image so the caller can
        // continue editing without a second click.
        const first = uploaded[0];
        const url = first.url || `/api/media/${first.path || first.filename}`;
        onSelect(url);
        onClose();
        return;
      }
      await fetchMedia();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUploading(false);
    }
  };

  const filtered = query.trim()
    ? media.filter((m) => (m.filename || m.path || '').toLowerCase().includes(query.toLowerCase()))
    : media;

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="media-picker-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col"
          data-testid="media-picker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-medium">Choose an image</h3>
              <p className="text-xs text-gray-500">{filtered.length} of {media.length} images</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
              data-testid="media-picker-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Toolbar */}
          <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search filename…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black"
                data-testid="media-picker-search"
              />
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-60 whitespace-nowrap"
              data-testid="media-picker-upload"
            >
              {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Upload new'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleUpload(e.target.files)}
              data-testid="media-picker-file-input"
            />
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader className="w-6 h-6 animate-spin mr-2" /> Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">
                No images match your search. Upload a new one above.
              </div>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {filtered.slice(0, 300).map((m) => {
                  const url = m.url || `/api/media/${m.path || m.filename}`;
                  const abs = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                  return (
                    <button
                      key={m.id || m.path}
                      type="button"
                      onClick={() => { onSelect(url); onClose(); }}
                      className="group relative aspect-square bg-gray-100 rounded-md overflow-hidden border border-gray-200 hover:border-black hover:shadow-md transition-all"
                      data-testid={`media-picker-item-${m.id || m.path}`}
                      title={m.filename || m.path}
                    >
                      <img
                        src={abs}
                        alt={m.filename || m.path}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-colors">
                        <Check className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 drop-shadow" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {filtered.length > 300 && (
              <p className="mt-4 text-center text-xs text-gray-500">
                Showing 300 of {filtered.length}. Refine your search to see more.
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MediaPickerModal;
